import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  FaBuilding, 
  FaUser, 
  FaPalette, 
  FaSitemap, 
  FaEnvelope, 
  FaCloudUploadAlt,
  FaGlobe,
  FaCalendarCheck,
  FaCheckCircle,
  FaPaperPlane,
  FaArrowRight,
  FaChevronRight,
  FaUpload,
  FaShieldAlt,
  FaRegClock,
  FaHandshake
} from 'react-icons/fa';
import './onboarding.css';

function OnboardingForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [progress, setProgress] = useState(12.5);
  const [formData, setFormData] = useState({
    // Business Overview
    businessName: '',
    businessType: '',
    tagline: '',
    businessDescription: '',
    
    // Contact Information
    contactPerson: '',
    phoneNumber: '',
    email: '',
    whatsappLink: '',
    location: '',
    
    // Branding Information
    hasLogo: '',
    primaryColor: '#3b82f6',
    secondaryColor: '#8b5cf6',
    preferredFont: '',
    websiteStyle: [],
    otherStyle: '',
    
    // Website Pages
    pages: ['Home', 'About', 'Contact'],
    otherPages: '',
    
    // Contact Form Behavior
    contactFormAction: ['Send to your email'],
    notificationEmail: '',
    
    // Domain & Hosting
    hasDomain: '',
    domainName: '',
    helpWithDomain: '',
    needsHosting: '',
    
    // Final Notes
    deadline: '',
    competitorWebsites: '',
    additionalNotes: ''
  });

  const totalSteps = 8;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (checked) {
        setFormData(prev => ({
          ...prev,
          [name]: [...prev[name], value]
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: prev[name].filter(item => item !== value)
        }));
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleRadioChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      setProgress(((currentStep + 1) / totalSteps) * 100);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setProgress(((currentStep - 1) / totalSteps) * 100);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    setProgress((step / totalSteps) * 100);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form submitted:', formData);
    
    // Show success animation/modal
    const successModal = document.createElement('div');
    successModal.className = 'success-modal';
    successModal.innerHTML = `
      <div class="success-content">
        <FaCheckCircle />
        <h3>Form Submitted Successfully!</h3>
        <p>We'll review your information and contact you within 24 hours.</p>
      </div>
    `;
    document.body.appendChild(successModal);
    
    setTimeout(() => {
      successModal.remove();
    }, 3000);
  };

  const steps = [
    { number: 1, title: "Business Overview", icon: <FaBuilding /> },
    { number: 2, title: "Contact Info", icon: <FaUser /> },
    { number: 3, title: "Branding", icon: <FaPalette /> },
    { number: 4, title: "Pages", icon: <FaSitemap /> },
    { number: 5, title: "Contact Form", icon: <FaEnvelope /> },
    { number: 6, title: "Content", icon: <FaCloudUploadAlt /> },
    { number: 7, title: "Domain & Hosting", icon: <FaGlobe /> },
    { number: 8, title: "Final Notes", icon: <FaCalendarCheck /> }
  ];

  const websiteStyles = [
    { id: 'styleProfessional', label: 'Professional', icon: '👔' },
    { id: 'styleModern', label: 'Modern', icon: '🚀' },
    { id: 'styleMinimal', label: 'Minimal', icon: '✨' },
    { id: 'styleFun', label: 'Fun', icon: '🎨' },
    { id: 'styleCorporate', label: 'Corporate', icon: '🏢' },
    { id: 'styleElegant', label: 'Elegant', icon: '💎' }
  ];

  const pageOptions = [
    { id: 'pageHome', label: 'Home', icon: '🏠' },
    { id: 'pageAbout', label: 'About', icon: '📖' },
    { id: 'pageServices', label: 'Services', icon: '🛠️' },
    { id: 'pageTestimonials', label: 'Testimonials', icon: '⭐' },
    { id: 'pageGallery', label: 'Gallery', icon: '🖼️' },
    { id: 'pageBlog', label: 'Blog', icon: '📝' },
    { id: 'pageContact', label: 'Contact', icon: '📞' },
    { id: 'pageFaqs', label: 'FAQs', icon: '❓' },
    { id: 'pageBooking', label: 'Booking', icon: '📅' },
    { id: 'pageEcommerce', label: 'E-commerce', icon: '🛒' }
  ];

  const renderStep = () => {
    switch(currentStep) {
      case 1:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="step-header">
              <div className="step-icon">
                <FaBuilding />
              </div>
              <div>
                <h3>Business Overview</h3>
                <p className="step-description">Tell us about your business</p>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Business Name *</label>
                <input 
                  type="text" 
                  name="businessName"
                  value={formData.businessName}
                  onChange={handleChange}
                  placeholder="e.g., Shyee Codes Agency"
                  className="modern-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Business Type *</label>
                <input 
                  type="text" 
                  name="businessType"
                  value={formData.businessType}
                  onChange={handleChange}
                  placeholder="e.g., Digital Agency, Restaurant, Law Firm"
                  className="modern-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Tagline / Slogan</label>
                <input 
                  type="text" 
                  name="tagline"
                  value={formData.tagline}
                  onChange={handleChange}
                  placeholder="Your business motto (optional)"
                  className="modern-input"
                />
              </div>
              
              <div className="form-group">
                <label>Business Description *</label>
                <textarea 
                  name="businessDescription"
                  value={formData.businessDescription}
                  onChange={handleChange}
                  placeholder="Describe what your business does, who you serve, and what makes you unique..."
                  className="modern-textarea"
                  rows="4"
                  required
                />
                <div className="character-count">
                  {formData.businessDescription.length}/500
                </div>
              </div>
            </div>
          </motion.div>
        );
        
      case 2:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="step-header">
              <div className="step-icon">
                <FaUser />
              </div>
              <div>
                <h3>Contact Information</h3>
                <p className="step-description">How we can reach you</p>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Primary Contact Person *</label>
                <input 
                  type="text" 
                  name="contactPerson"
                  value={formData.contactPerson}
                  onChange={handleChange}
                  placeholder="e.g., John Doe"
                  className="modern-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Phone Number *</label>
                <input 
                  type="tel" 
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="e.g., +254 712 345 678"
                  className="modern-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>Email Address *</label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="hello@yourbusiness.com"
                  className="modern-input"
                  required
                />
              </div>
              
              <div className="form-group">
                <label>WhatsApp Link</label>
                <input 
                  type="url" 
                  name="whatsappLink"
                  value={formData.whatsappLink}
                  onChange={handleChange}
                  placeholder="https://wa.me/254712345678"
                  className="modern-input"
                />
              </div>
              
              <div className="form-group">
                <label>Physical Location / Region</label>
                <input 
                  type="text" 
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  placeholder="e.g., Nairobi, Kenya"
                  className="modern-input"
                />
              </div>
            </div>
          </motion.div>
        );
        
      case 3:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="step-header">
              <div className="step-icon">
                <FaPalette />
              </div>
              <div>
                <h3>Branding Information</h3>
                <p className="step-description">Define your visual identity</p>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="radio-label">Do you have a logo?</label>
                <div className="radio-group">
                  <label className={`radio-option ${formData.hasLogo === 'yes' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="hasLogo" 
                      value="yes"
                      checked={formData.hasLogo === 'yes'}
                      onChange={handleRadioChange}
                    />
                    <span className="radio-dot"></span>
                    <span>Yes</span>
                  </label>
                  <label className={`radio-option ${formData.hasLogo === 'no' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="hasLogo" 
                      value="no"
                      checked={formData.hasLogo === 'no'}
                      onChange={handleRadioChange}
                    />
                    <span className="radio-dot"></span>
                    <span>No</span>
                  </label>
                </div>
                <small className="helper-text">If yes, we'll request it after submission</small>
              </div>
              
              <div className="form-group">
                <label>Primary Brand Color</label>
                <div className="color-picker-wrapper">
                  <input 
                    type="color" 
                    name="primaryColor"
                    value={formData.primaryColor}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input 
                    type="text" 
                    name="primaryColor"
                    value={formData.primaryColor}
                    onChange={handleChange}
                    placeholder="#3b82f6"
                    className="color-input"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Secondary Color</label>
                <div className="color-picker-wrapper">
                  <input 
                    type="color" 
                    name="secondaryColor"
                    value={formData.secondaryColor}
                    onChange={handleChange}
                    className="color-picker"
                  />
                  <input 
                    type="text" 
                    name="secondaryColor"
                    value={formData.secondaryColor}
                    onChange={handleChange}
                    placeholder="#8b5cf6"
                    className="color-input"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label>Preferred Font</label>
                <select 
                  name="preferredFont"
                  value={formData.preferredFont}
                  onChange={handleChange}
                  className="modern-select"
                >
                  <option value="">Select a font</option>
                  <option value="Montserrat">Montserrat (Modern)</option>
                  <option value="Poppins">Poppins (Clean)</option>
                  <option value="Inter">Inter (Professional)</option>
                  <option value="Playfair Display">Playfair Display (Elegant)</option>
                  <option value="Roboto">Roboto (Versatile)</option>
                  <option value="Open Sans">Open Sans (Readable)</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Website Style Preference</label>
                <div className="style-grid">
                  {websiteStyles.map((style) => (
                    <label 
                      key={style.id}
                      className={`style-option ${formData.websiteStyle.includes(style.label) ? 'selected' : ''}`}
                    >
                      <input 
                        type="checkbox"
                        name="websiteStyle"
                        value={style.label}
                        checked={formData.websiteStyle.includes(style.label)}
                        onChange={handleChange}
                        className="hidden-checkbox"
                      />
                      <span className="style-icon">{style.icon}</span>
                      <span className="style-label">{style.label}</span>
                    </label>
                  ))}
                </div>
                {formData.websiteStyle.includes('Other') && (
                  <div className="mt-3">
                    <input 
                      type="text" 
                      name="otherStyle"
                      value={formData.otherStyle}
                      onChange={handleChange}
                      placeholder="Describe other style..."
                      className="modern-input"
                    />
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        );
        
      case 4:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="step-header">
              <div className="step-icon">
                <FaSitemap />
              </div>
              <div>
                <h3>Website Pages & Features</h3>
                <p className="step-description">Select required pages</p>
              </div>
            </div>
            
            <div className="form-group">
              <label>Which pages should we include?</label>
              <div className="pages-grid">
                {pageOptions.map((page) => (
                  <label 
                    key={page.id}
                    className={`page-option ${formData.pages.includes(page.label) ? 'selected' : ''}`}
                  >
                    <input 
                      type="checkbox"
                      name="pages"
                      value={page.label}
                      checked={formData.pages.includes(page.label)}
                      onChange={handleChange}
                      className="hidden-checkbox"
                    />
                    <span className="page-icon">{page.icon}</span>
                    <span className="page-label">{page.label}</span>
                  </label>
                ))}
              </div>
              {formData.pages.includes('Other') && (
                <div className="mt-3">
                  <input 
                    type="text" 
                    name="otherPages"
                    value={formData.otherPages}
                    onChange={handleChange}
                    placeholder="Specify other pages..."
                    className="modern-input"
                  />
                </div>
              )}
            </div>
          </motion.div>
        );
        
      case 5:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="step-header">
              <div className="step-icon">
                <FaEnvelope />
              </div>
              <div>
                <h3>Contact Form Behavior</h3>
                <p className="step-description">How should we handle inquiries?</p>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>When someone fills the contact form:</label>
                <div className="action-grid">
                  <label className={`action-option ${formData.contactFormAction.includes('Send to your email') ? 'selected' : ''}`}>
                    <input 
                      type="checkbox"
                      name="contactFormAction"
                      value="Send to your email"
                      checked={formData.contactFormAction.includes('Send to your email')}
                      onChange={handleChange}
                      className="hidden-checkbox"
                    />
                    <div className="action-content">
                      <span className="action-icon">📧</span>
                      <span className="action-title">Email Notification</span>
                      <span className="action-desc">Receive inquiries directly in your inbox</span>
                    </div>
                  </label>
                  
                  <label className={`action-option ${formData.contactFormAction.includes('Save in admin dashboard') ? 'selected' : ''}`}>
                    <input 
                      type="checkbox"
                      name="contactFormAction"
                      value="Save in admin dashboard"
                      checked={formData.contactFormAction.includes('Save in admin dashboard')}
                      onChange={handleChange}
                      className="hidden-checkbox"
                    />
                    <div className="action-content">
                      <span className="action-icon">📊</span>
                      <span className="action-title">Admin Dashboard</span>
                      <span className="action-desc">Manage all inquiries in one place</span>
                    </div>
                  </label>
                  
                  <label className={`action-option ${formData.contactFormAction.includes('WhatsApp notification') ? 'selected' : ''}`}>
                    <input 
                      type="checkbox"
                      name="contactFormAction"
                      value="WhatsApp notification"
                      checked={formData.contactFormAction.includes('WhatsApp notification')}
                      onChange={handleChange}
                      className="hidden-checkbox"
                    />
                    <div className="action-content">
                      <span className="action-icon">💬</span>
                      <span className="action-title">WhatsApp Alert</span>
                      <span className="action-desc">Get instant notifications on WhatsApp</span>
                    </div>
                  </label>
                </div>
              </div>
              
              <div className="form-group">
                <label>Notification Email</label>
                <input 
                  type="email" 
                  name="notificationEmail"
                  value={formData.notificationEmail}
                  onChange={handleChange}
                  placeholder="notifications@yourbusiness.com"
                  className="modern-input"
                />
              </div>
            </div>
          </motion.div>
        );
        
      case 6:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="step-header">
              <div className="step-icon">
                <FaCloudUploadAlt />
              </div>
              <div>
                <h3>Content & Uploads</h3>
                <p className="step-description">Prepare your materials</p>
              </div>
            </div>
            
            <div className="upload-card">
              <div className="upload-icon">
                <FaUpload />
              </div>
              <h4>What You Need to Provide</h4>
              <p className="upload-description">
                For the best results, please prepare the following materials:
              </p>
              
              <ul className="upload-list">
                <li>✅ Business profile and company information</li>
                <li>✅ Text content for each selected page</li>
                <li>✅ High-quality images and team photos</li>
                <li>✅ Product/service details and pricing</li>
                <li>✅ Social media links and handles</li>
                <li>✅ Any existing marketing materials</li>
              </ul>
              
              <div className="upload-options">
                <button type="button" className="upload-btn">
                  <FaUpload className="me-2" />
                  Upload Files
                </button>
                <span className="upload-note">
                  You can also send files via email or file sharing services
                </span>
              </div>
            </div>
          </motion.div>
        );
        
      case 7:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="step-header">
              <div className="step-icon">
                <FaGlobe />
              </div>
              <div>
                <h3>Domain & Hosting</h3>
                <p className="step-description">Your online presence setup</p>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label className="radio-label">Do you own a domain name?</label>
                <div className="radio-group">
                  <label className={`radio-option ${formData.hasDomain === 'yes' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="hasDomain" 
                      value="yes"
                      checked={formData.hasDomain === 'yes'}
                      onChange={handleRadioChange}
                    />
                    <span className="radio-dot"></span>
                    <span>Yes, I own one</span>
                  </label>
                  <label className={`radio-option ${formData.hasDomain === 'no' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="hasDomain" 
                      value="no"
                      checked={formData.hasDomain === 'no'}
                      onChange={handleRadioChange}
                    />
                    <span className="radio-dot"></span>
                    <span>No, I need one</span>
                  </label>
                </div>
                
                {formData.hasDomain === 'yes' && (
                  <div className="mt-3">
                    <input 
                      type="text" 
                      name="domainName"
                      value={formData.domainName}
                      onChange={handleChange}
                      placeholder="yourdomain.com"
                      className="modern-input"
                    />
                  </div>
                )}
                
                {formData.hasDomain === 'no' && (
                  <div className="mt-3">
                    <label className="radio-label">Need help purchasing?</label>
                    <div className="radio-group">
                      <label className={`radio-option ${formData.helpWithDomain === 'yes' ? 'selected' : ''}`}>
                        <input 
                          type="radio" 
                          name="helpWithDomain" 
                          value="yes"
                          checked={formData.helpWithDomain === 'yes'}
                          onChange={handleRadioChange}
                        />
                        <span className="radio-dot"></span>
                        <span>Yes, please assist</span>
                      </label>
                      <label className={`radio-option ${formData.helpWithDomain === 'no' ? 'selected' : ''}`}>
                        <input 
                          type="radio" 
                          name="helpWithDomain" 
                          value="no"
                          checked={formData.helpWithDomain === 'no'}
                          onChange={handleRadioChange}
                        />
                        <span className="radio-dot"></span>
                        <span>I'll handle it myself</span>
                      </label>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="form-group">
                <label className="radio-label">Web Hosting</label>
                <div className="radio-group vertical">
                  <label className={`radio-option ${formData.needsHosting === 'yes' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="needsHosting" 
                      value="yes"
                      checked={formData.needsHosting === 'yes'}
                      onChange={handleRadioChange}
                    />
                    <span className="radio-dot"></span>
                    <div className="radio-content">
                      <span className="radio-title">Managed Hosting</span>
                      <span className="radio-desc">We'll handle everything for you</span>
                    </div>
                  </label>
                  
                  <label className={`radio-option ${formData.needsHosting === 'no' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="needsHosting" 
                      value="no"
                      checked={formData.needsHosting === 'no'}
                      onChange={handleRadioChange}
                    />
                    <span className="radio-dot"></span>
                    <div className="radio-content">
                      <span className="radio-title">I Have Hosting</span>
                      <span className="radio-desc">I'll provide hosting details</span>
                    </div>
                  </label>
                  
                  <label className={`radio-option ${formData.needsHosting === 'not sure' ? 'selected' : ''}`}>
                    <input 
                      type="radio" 
                      name="needsHosting" 
                      value="not sure"
                      checked={formData.needsHosting === 'not sure'}
                      onChange={handleRadioChange}
                    />
                    <span className="radio-dot"></span>
                    <div className="radio-content">
                      <span className="radio-title">Not Sure</span>
                      <span className="radio-desc">Need recommendations</span>
                    </div>
                  </label>
                </div>
              </div>
            </div>
          </motion.div>
        );
        
      case 8:
        return (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className="step-header">
              <div className="step-icon">
                <FaCalendarCheck />
              </div>
              <div>
                <h3>Final Notes & Preferences</h3>
                <p className="step-description">Almost there! Final details</p>
              </div>
            </div>
            
            <div className="form-grid">
              <div className="form-group">
                <label>Target Launch Date</label>
                <input 
                  type="date" 
                  name="deadline"
                  value={formData.deadline}
                  onChange={handleChange}
                  className="modern-input"
                />
                <small className="helper-text">When would you like to launch?</small>
              </div>
              
              <div className="form-group">
                <label>Reference Websites</label>
                <textarea 
                  name="competitorWebsites"
                  value={formData.competitorWebsites}
                  onChange={handleChange}
                  placeholder="Paste links to websites you like for design inspiration..."
                  className="modern-textarea"
                  rows="3"
                />
              </div>
              
              <div className="form-group">
                <label>Additional Notes & Special Requests</label>
                <textarea 
                  name="additionalNotes"
                  value={formData.additionalNotes}
                  onChange={handleChange}
                  placeholder="Any specific features, integrations, or special requirements..."
                  className="modern-textarea"
                  rows="4"
                />
                <div className="character-count">
                  {formData.additionalNotes.length}/1000
                </div>
              </div>
            </div>
          </motion.div>
        );
        
      default:
        return null;
    }
  };

  return (
    <div className="onboarding-container">
      <div className="onboarding-header">
        <div className="header-content">
          <br/><br/><br/>

          <h1>Start Your Project</h1>
          <p className="header-subtitle">
            Complete this form to begin your digital transformation journey with Shyee Codes
          </p>
        </div>
        
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <div className="progress-text">
            Step {currentStep} of {totalSteps} • {Math.round(progress)}% Complete
          </div>
        </div>
      </div>
      
      <div className="onboarding-content">
        <div className="sidebar">
          <div className="sidebar-header">
            <h3>Project Setup</h3>
            <div className="completion-badge">
              <FaRegClock />
              <span>15-20 mins</span>
            </div>
          </div>
          
          <div className="steps-list">
            {steps.map((step) => (
              <button
                key={step.number}
                className={`step-item ${currentStep === step.number ? 'active' : ''} ${step.number < currentStep ? 'completed' : ''}`}
                onClick={() => goToStep(step.number)}
              >
                <div className="step-number">
                  {step.number < currentStep ? (
                    <FaCheckCircle />
                  ) : (
                    <span>{step.number}</span>
                  )}
                </div>
                <div className="step-info">
                  <span className="step-title">{step.title}</span>
                  {step.number < currentStep && (
                    <span className="step-status">Completed</span>
                  )}
                </div>
                {currentStep === step.number && (
                  <FaChevronRight className="step-arrow" />
                )}
              </button>
            ))}
          </div>
          
          <div className="sidebar-footer">
            <div className="support-card">
              <FaShieldAlt className="support-icon" />
              <div>
                <h4>Need Help?</h4>
                <p>Our team is here to assist you</p>
              </div>
              <a href="https://wa.me/254762272152" className="support-button">
                WhatsApp Support
              </a>
            </div>
          </div>
        </div>
        
        <div className="form-container">
          <form onSubmit={handleSubmit}>
            {renderStep()}
            
            <div className="form-navigation">
              {currentStep > 1 && (
                <button 
                  type="button" 
                  onClick={prevStep}
                  className="nav-button secondary"
                >
                  Previous Step
                </button>
              )}
              
              <div className="navigation-right">
                {currentStep < totalSteps ? (
                  <button 
                    type="button" 
                    onClick={nextStep}
                    className="nav-button primary"
                  >
                    Continue to Next Step
                    <FaArrowRight />
                  </button>
                ) : (
                  <button 
                    type="submit" 
                    className="submit-button"
                  >
                    <FaPaperPlane />
                    Submit Project Request
                  </button>
                )}
              </div>
            </div>
          </form>
          
          {currentStep === totalSteps && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="next-steps-card"
            >
              <div className="next-steps-header">
                <FaHandshake />
                <h4>What Happens Next?</h4>
              </div>
              <ol className="next-steps-list">
                <li>
                  <strong>Review & Consultation</strong>
                  <p>We'll analyze your requirements and schedule a discovery call</p>
                </li>
                <li>
                  <strong>Proposal & Timeline</strong>
                  <p>Receive a detailed proposal with timeline and investment</p>
                </li>
                <li>
                  <strong>Design & Development</strong>
                  <p>We'll start building your custom solution</p>
                </li>
                <li>
                  <strong>Launch & Support</strong>
                  <p>Your website goes live with ongoing support</p>
                </li>
              </ol>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

export default OnboardingForm;